import {Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {map, take} from 'rxjs/operators';
import type {MessengerConversationOrganisationPort} from './conversation-organisation.port';
import {ConversationCall, ConversationHit, ConversationScope, MutedUntil} from './messenger.models';
import {fixtureIndex} from './messenger-fixture-data';
import {CollaborationState} from '../store/collaboration.reducer';
import {getConversationListInfo} from '../store/collaboration.selector';

/** One conversation's organisation marks, as the fixture holds them for this browser tab. */
interface FixtureMarks {
  pinned?: boolean;
  mutedUntil?: MutedUntil | null;
  archived?: boolean;
  markedUnread?: boolean;
}

/**
 * The conversation organisation port on the carbon mockup's data, for a dev build (ITP-E-194-02). Marks start from a
 * stable sample per conversation, as the mockup pins two conversations of seven and mutes one, and change in memory
 * until the page reloads.
 */
@Injectable({providedIn: 'root'})
export class MessengerConversationOrganisationFixture implements MessengerConversationOrganisationPort {
  private readonly marks$ = new BehaviorSubject<Record<string, FixtureMarks>>({});

  constructor(private collaborationStore: Store<CollaborationState>) {
  }

  /** Today's loaded conversations, by title or member name. */
  search(text: string): Observable<ConversationHit[]> {
    const query = (text || '').trim().toLowerCase();
    return this.collaborationStore.select(getConversationListInfo).pipe(take(1), map(info => {
      if (!query) {
        return [];
      }
      const hits: ConversationHit[] = [];
      (info?.queryConversations || []).forEach((conversation: any) => {
        if ((conversation.title || '').toLowerCase().includes(query)) {
          hits.push({conversationId: conversation.id, title: conversation.title, matchedOn: 'title'});
        } else if ((conversation.roles || []).some(role =>
          `${role?.user?.firstName || ''} ${role?.user?.lastName || ''}`.toLowerCase().includes(query))) {
          hits.push({conversationId: conversation.id, title: conversation.title, matchedOn: 'member'});
        }
      });
      return hits;
    }));
  }

  unreadMentions(conversationIds: string[]): Observable<Record<string, boolean>> {
    return of(this.sample(conversationIds, id => fixtureIndex(`${id}:mention`, 4) === 0));
  }

  callHistory(conversationId: string): Observable<ConversationCall[]> {
    return of([
      {invitationId: `${conversationId}-c2`, type: 'Video', outcome: 'Answered', startedAt: '2026-10-07T10:31:00Z', durationSeconds: 1260},
      {invitationId: `${conversationId}-c1`, type: 'Voice', outcome: 'Missed', startedAt: '2026-10-06T17:42:00Z'}
    ]);
  }

  scopes(conversationIds: string[]): Observable<Record<string, ConversationScope>> {
    const scopes: Record<string, ConversationScope> = {};
    conversationIds.filter(id => fixtureIndex(`${id}:scope`, 5) === 0)
      .forEach(id => scopes[id] = {kind: 'Mission', key: 'UK-QDMTT-FY25', label: 'Mission'});
    return of(scopes);
  }

  pinned(conversationIds: string[]): Observable<Record<string, boolean>> {
    return this.read(conversationIds, (marks, id) => marks.pinned ?? fixtureIndex(`${id}:pin`, 4) === 0);
  }

  setPinned(conversationId: string, pinned: boolean): Observable<void> {
    return this.write(conversationId, {pinned});
  }

  mutedUntil(conversationIds: string[]): Observable<Record<string, MutedUntil>> {
    return this.marks$.pipe(map(marks => {
      const muted: Record<string, MutedUntil> = {};
      conversationIds.forEach(id => {
        const set = marks[id] && marks[id].mutedUntil !== undefined;
        const until = set ? marks[id].mutedUntil : fixtureIndex(`${id}:mute`, 6) === 0 ? 'always' : null;
        if (until) {
          muted[id] = until;
        }
      });
      return muted;
    }));
  }

  mute(conversationId: string, until: MutedUntil | null): Observable<void> {
    return this.write(conversationId, {mutedUntil: until});
  }

  archived(conversationIds: string[]): Observable<Record<string, boolean>> {
    return this.read(conversationIds, marks => !!marks.archived);
  }

  setArchived(conversationId: string, archived: boolean): Observable<void> {
    return this.write(conversationId, {archived});
  }

  markedUnread(conversationIds: string[]): Observable<Record<string, boolean>> {
    return this.read(conversationIds, marks => !!marks.markedUnread);
  }

  setMarkedUnread(conversationId: string, unread: boolean): Observable<void> {
    return this.write(conversationId, {markedUnread: unread});
  }

  private sample(conversationIds: string[], test: (id: string) => boolean): Record<string, boolean> {
    const result: Record<string, boolean> = {};
    conversationIds.forEach(id => result[id] = test(id));
    return result;
  }

  private read(conversationIds: string[], value: (marks: FixtureMarks, id: string) => boolean): Observable<Record<string, boolean>> {
    return this.marks$.pipe(map(marks => this.sample(conversationIds, id => value(marks[id] || {}, id))));
  }

  private write(conversationId: string, change: FixtureMarks): Observable<void> {
    const marks = this.marks$.value;
    this.marks$.next({...marks, [conversationId]: {...marks[conversationId], ...change}});
    return of(undefined);
  }
}
